import { useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, CircularProgress,
  Collapse, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { useProjektBoards, unwrapProjektBoards, useCreateProjektBoard, projektKanbanFehler } from '../../../../api/hooks/useProjektKanban';
import { useNotifications } from '../../../../context/NotificationContext';
import { KanbanBoardView } from './KanbanBoardView';

interface Props {
  projectId: string;
  showHidden: boolean;
  /** Personen-Picker-Optionen (P-14). */
  personOptions?: string[];
}

/**
 * PHQ-104: Projekt-Kanban ohne Arbeitspaket. Zeigt das aktive Board (beschreibbar),
 * „Neue Woche" friert es ein und legt das nächste an; eingefrorene Boards stehen
 * als Historie, nur lesbar.
 */
export function ProjektBoardView({ projectId, showHidden, personOptions = [] }: Props) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const { data, isLoading } = useProjektBoards(projectId);
  const createBoard = useCreateProjektBoard(projectId);
  const [openFrozen, setOpenFrozen] = useState<Record<string, boolean>>({});

  const boards = unwrapProjektBoards(data); // seq DESC
  const active = boards.find((b) => !b.frozen);
  const frozen = boards.filter((b) => b.id !== active?.id);

  const handleNewBoard = async () => {
    try {
      await createBoard.mutateAsync();
      notify(t('kanban.boardCreated', 'Neues Board angelegt'), 'success');
    } catch (err) { notify(projektKanbanFehler(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" fontWeight={600}>
          {active ? `${active.label} · ${t('kanban.activeBoard', 'aktiv')}` : t('kanban.projectBoard', 'Projekt-Board')}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => void handleNewBoard()} disabled={createBoard.isPending}>
          {active ? t('kanban.newWeek', 'Neue Woche') : t('kanban.firstBoard', 'Board anlegen')}
        </Button>
      </Stack>

      {!active ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {t('kanban.noProjectBoard', 'Noch kein Projekt-Board. „Board anlegen" erstellt das erste.')}
        </Typography>
      ) : (
        <Box sx={{ mb: frozen.length ? 2 : 0 }}>
          <KanbanBoardView projectId={projectId} apId={null} board={active} active showHidden={showHidden} personOptions={personOptions} />
        </Box>
      )}

      {frozen.map((board) => {
        const isOpen = !!openFrozen[board.id];
        return (
          <Box key={board.id} sx={{ borderTop: '1px dashed var(--pm-border)', pt: 1, mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title={isOpen ? tc('actions.collapse', 'Einklappen') : tc('actions.expand', 'Ausklappen')}>
                <IconButton size="small" onClick={() => setOpenFrozen((p) => ({ ...p, [board.id]: !isOpen }))}>
                  {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <LockIcon sx={{ fontSize: 15, color: 'var(--pm-fg-subtle)' }} />
              <Typography variant="caption" color="text.secondary">
                {board.label} · {t('kanban.frozen', 'eingefroren')} · {board.cards.length} {t('kanban.cards', 'Karten')}
              </Typography>
            </Stack>
            <Collapse in={isOpen} unmountOnExit>
              <Box sx={{ mt: 1, opacity: 0.85 }}>
                <KanbanBoardView projectId={projectId} apId={null} board={board} active={false} showHidden={showHidden} personOptions={personOptions} />
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Paper>
  );
}
